import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Collapse from "@mui/material/Collapse";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { getCategories } from "../../services/Public/PublicPagesApi";
import { resolveImageUrl } from "../../services/indexMediaBaseUrl";

function CategoryNode({ cat, level = 0, isAr, expandedIds, setExpandedIds, navigate, closeMenu }) {
  const children = Array.isArray(cat?.children_recursive) ? cat.children_recursive.filter(c => c.status !== 0) : [];
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(cat.id);

  const name = isAr
    ? cat?.name_ar || cat?.name_en || `#${cat?.id}`
    : cat?.name_en || cat?.name_ar || `#${cat?.id}`;

  const imgSrc = resolveImageUrl(cat?.image);

  const toggle = (e) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cat.id)) next.delete(cat.id);
      else next.add(cat.id);
      return next;
    });
  };

  const go = () => {
    closeMenu();
    navigate(`/category/${cat.id}`);
  };

  return (
    <>
      <ListItemButton
        onClick={go}
        sx={{
          pl: 2 + level * 2.5,
          pr: 1,
          py: 0.8,
          borderRadius: 2,
          mx: 0.5,
          my: 0.3,
          transition: "all 0.2s ease",
          borderInlineStart: level > 0 ? "2px solid" : "none",
          borderColor: level > 0 ? "grey.200" : "transparent",
          "&:hover": {
            bgcolor: "primary.50",
            borderColor: "primary.main",
          },
        }}
      >
        {imgSrc && (
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Avatar src={imgSrc} sx={{ width: 24, height: 24 }} />
          </ListItemIcon>
        )}

        <ListItemText
          primary={name}
          primaryTypographyProps={{
            noWrap: true,
            sx: {
              fontSize: level === 0 ? "0.9rem" : "0.85rem",
              fontWeight: level === 0 ? 700 : 600,
              color: level === 0 ? "text.primary" : "text.secondary",
            },
          }}
        />

        {hasChildren && (
          <IconButton
            size="small"
            onClick={toggle}
            sx={{
              ml: 0.5,
              transition: "transform 0.3s ease",
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              color: "primary.main",
              "&:hover": { bgcolor: "primary.50" },
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        )}
      </ListItemButton>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {children.map((ch) => (
              <CategoryNode
                key={ch.id}
                cat={ch}
                level={level + 1}
                isAr={isAr}
                expandedIds={expandedIds}
                setExpandedIds={setExpandedIds}
                navigate={navigate}
                closeMenu={closeMenu}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

export default function CategoriesNavbar() {
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const storedLang = (localStorage.getItem("lang") || "ar").toLowerCase();
  const isAr = storedLang.startsWith("ar");

  // Desktop menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeParent, setActiveParent] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const closeTimerRef = useRef(null);

  // Mobile drawer state
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileActiveParent, setMobileActiveParent] = useState(null);
  const [mobileExpandedIds, setMobileExpandedIds] = useState(() => new Set());

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = (delay = 200) => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setAnchorEl(null);
      setActiveParent(null);
      setExpandedIds(new Set());
      closeTimerRef.current = null;
    }, delay);
  };

  useEffect(() => {
    let mounted = true;
    const fetchCats = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await getCategories();
        const list = res?.data?.data || res?.data || [];
        if (mounted) {
          // Filter out categories with status 0
          const filtered = Array.isArray(list) ? list.filter(cat => cat.status !== 0) : [];
          setParents(filtered);
        }
      } catch (err) {
        if (mounted) setError(err?.response?.data?.message || err?.message || "فشل جلب الفئات");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchCats();
    return () => { mounted = false; };
  }, []);

  // --- Desktop handlers ---
  const handleDesktopHover = (e, parent) => {
    const children = Array.isArray(parent?.children_recursive) ? parent.children_recursive : [];
    if (!children.length) return; // no submenu needed
    clearCloseTimer();
    setAnchorEl(e.currentTarget);
    setActiveParent(parent);
    setExpandedIds(new Set());
  };

  const handleDesktopClick = (parent) => {
    // Always navigate on click
    setAnchorEl(null);
    setActiveParent(null);
    setExpandedIds(new Set());
    navigate(`/category/${parent.id}`);
  };

  const closeMenuNow = () => {
    setAnchorEl(null);
    setActiveParent(null);
    setExpandedIds(new Set());
  };

  // --- Mobile handlers ---
  const handleMobileTap = (parent) => {
    const children = Array.isArray(parent?.children_recursive) ? parent.children_recursive : [];
    if (!children.length) {
      navigate(`/category/${parent.id}`);
      return;
    }
    setMobileActiveParent(parent);
    setMobileExpandedIds(new Set());
    setMobileDrawerOpen(true);
  };

  const closeMobileDrawer = () => {
    setMobileDrawerOpen(false);
    setMobileActiveParent(null);
    setMobileExpandedIds(new Set());
  };

  const parentName = (p) =>
    isAr ? p?.name_ar || p?.name_en || `#${p?.id}` : p?.name_en || p?.name_ar || `#${p?.id}`;

  // --- Shared tree content renderer ---
  const renderTree = (parent, expIds, setExpIds, closeFn) => (
    <Box sx={{ px: 0.5, py: 1 }}>
      {/* Parent header — navigates */}
      <ListItemButton
        onClick={() => { closeFn(); navigate(`/category/${parent.id}`); }}
        sx={{
          borderRadius: 2,
          mx: 0.5,
          mb: 1,
          bgcolor: "primary.50",
          "&:hover": { bgcolor: "primary.light" },
        }}
      >
        {resolveImageUrl(parent.image) && (
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Avatar src={resolveImageUrl(parent.image)} sx={{ width: 28, height: 28 }} />
          </ListItemIcon>
        )}
        <ListItemText
          primary={parentName(parent)}
          secondary={isAr ? "عرض الكل" : "View all"}
          primaryTypographyProps={{ fontWeight: 800, fontSize: "0.95rem", color: "primary.dark" }}
          secondaryTypographyProps={{ fontWeight: 600, fontSize: "0.7rem" }}
        />
        <ChevronRightIcon fontSize="small" sx={{ color: "primary.main" }} />
      </ListItemButton>

      {Array.isArray(parent.children_recursive) && parent.children_recursive.filter(c => c.status !== 0).length > 0 ? (
        <List disablePadding>
          {parent.children_recursive.filter(c => c.status !== 0).map((child) => (
            <CategoryNode
              key={child.id}
              cat={child}
              level={0}
              isAr={isAr}
              expandedIds={expIds}
              setExpandedIds={setExpIds}
              navigate={navigate}
              closeMenu={closeFn}
            />
          ))}
        </List>
      ) : (
        <Typography sx={{ px: 2, py: 1, color: "text.secondary", fontSize: "0.85rem" }}>
          {isAr ? "لا توجد فئات فرعية" : "No subcategories"}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "grey.100",
          bgcolor: "background.paper",
          boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
        }}
        onMouseEnter={!isMobile ? clearCloseTimer : undefined}
        onMouseLeave={!isMobile ? () => scheduleClose(200) : undefined}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
            <CircularProgress size={20} sx={{ color: "primary.main" }} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 1 }}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              gap: { xs: 0.5, md: 0.5 },
              overflowX: "auto",
              px: { xs: 1, md: 2 },
              py: 1,
              alignItems: "center",
              "&::-webkit-scrollbar": { height: 4 },
              "&::-webkit-scrollbar-thumb": { bgcolor: "grey.300", borderRadius: 2 },
            }}
          >
            {parents.length ? (
              parents.map((p) => {
                const imgSrc = resolveImageUrl(p.image);
                const hasChildren = Array.isArray(p?.children_recursive) && p.children_recursive.filter(c => c.status !== 0).length > 0;
                const isActive = activeParent?.id === p.id;

                return (
                  <Button
                    key={p.id}
                    variant="text"
                    size="small"
                    sx={{
                      whiteSpace: "nowrap",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: { xs: "0.8rem", md: "0.875rem" },
                      borderRadius: 2,
                      px: { xs: 1.5, md: 2 },
                      py: 0.8,
                      minWidth: "auto",
                      color: isActive ? "primary.contrastText" : "text.primary",
                      bgcolor: isActive ? "primary.main" : "transparent",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        bgcolor: isActive ? "primary.dark" : "primary.50",
                        color: isActive ? "primary.contrastText" : "primary.dark",
                      },
                    }}
                    startIcon={imgSrc ? <Avatar src={imgSrc} sx={{ width: 20, height: 20 }} /> : undefined}
                    endIcon={hasChildren ? <ExpandMoreIcon sx={{ fontSize: "1rem" }} /> : undefined}
                    onMouseEnter={!isMobile ? (e) => handleDesktopHover(e, p) : undefined}
                    onClick={() => {
                      if (isMobile) {
                        handleMobileTap(p);
                      } else {
                        handleDesktopClick(p);
                      }
                    }}
                  >
                    {parentName(p)}
                  </Button>
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                {isAr ? "لا توجد فئات" : "No categories"}
              </Typography>
            )}

            {/* Desktop Menu */}
            {!isMobile && (
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl) && Boolean(activeParent)}
                onClose={closeMenuNow}
                MenuListProps={{
                  onMouseEnter: clearCloseTimer,
                  onMouseLeave: () => scheduleClose(200),
                  sx: { py: 0.5 },
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: {
                    width: 340,
                    maxHeight: 480,
                    overflow: "auto",
                    borderRadius: 3,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    border: "1px solid",
                    borderColor: "grey.100",
                    mt: 0.5,
                  },
                }}
              >
                {activeParent && renderTree(activeParent, expandedIds, setExpandedIds, closeMenuNow)}
              </Menu>
            )}
          </Box>
        )}
      </Box>

      {/* Mobile Drawer for subcategories */}
      <Drawer
        anchor="bottom"
        open={mobileDrawerOpen}
        onClose={closeMobileDrawer}
        PaperProps={{
          sx: {
            borderRadius: "20px 20px 0 0",
            maxHeight: "75vh",
            overflow: "auto",
          },
        }}
      >
        {/* Drawer handle */}
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 0.5 }}>
          <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: "grey.300" }} />
        </Box>

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "grey.100",
          }}
        >
          <Typography variant="h6" fontWeight={800} color="primary.dark">
            {mobileActiveParent ? parentName(mobileActiveParent) : ""}
          </Typography>
          <IconButton onClick={closeMobileDrawer} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tree content */}
        <Box sx={{ pb: 3 }}>
          {mobileActiveParent && renderTree(
            mobileActiveParent,
            mobileExpandedIds,
            setMobileExpandedIds,
            closeMobileDrawer
          )}
        </Box>
      </Drawer>
    </>
  );
}
